import { AccredibleResponsiveMenuItem } from '@accredible-frontend-v2/components/responsive-menu';
import { environment } from '@accredible-frontend-v2/envs';
import { AccredibleUser } from '@accredible-frontend-v2/models';
import {
  AccountsRedirectionKey,
  AccredibleAccountsRedirectionService,
} from '@accredible-frontend-v2/services/accounts-redirection';
import { AccredibleAuthenticationApiService } from '@accredible-frontend-v2/services/authentication';
import { AccredibleBrowserStorageService } from '@accredible-frontend-v2/services/browser-storage';
import { AccredibleCookiesService } from '@accredible-frontend-v2/services/cookies';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { AccredibleToastService } from '@accredible-frontend-v2/toast';
import { AccredibleUrlHelper } from '@accredible-frontend-v2/utils/url-helper';
import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, inject, Inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { first, map, switchMap } from 'rxjs';
import * as AuthorizedActions from '../../../core/stores/authorized/authorized.actions';
import { AuthorizedState } from '../../stores/authorized/authorized.reducer';
import { selectUser } from '../../stores/authorized/authorized.selectors';

@Component({
  selector: 'acs-header-menu',
  templateUrl: './header-menu.component.html',
  styleUrls: [`./header-menu.component.scss`],
  // ChangeDetectionStrategy.OnPush can't be activated (in both this and accredible-base-header-menu components) due to an issue in the MaterialMenu Component.
  // If activated the attribute aria-expanded="true" is not removed
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderMenuComponent implements OnInit {
  private readonly _accountsRedirection = inject(AccredibleAccountsRedirectionService);
  private readonly _authorizedStore = inject(Store<AuthorizedState>);
  private readonly _authenticationApi = inject(AccredibleAuthenticationApiService);
  private readonly _router = inject(Router);
  private readonly _browserStorage = inject(AccredibleBrowserStorageService);
  private readonly _cookies = inject(AccredibleCookiesService);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _toast = inject(AccredibleToastService);
  private readonly _destroyRef = inject(DestroyRef);

  @Input()
  extraDynamicMenuItems: AccredibleResponsiveMenuItem[];

  readonly user$ = this._authorizedStore.select(selectUser);
  readonly languageCode$ = this._language.languageCode$;

  dynamicMenuItems: AccredibleResponsiveMenuItem[] = [];
  staticMenuItems: AccredibleResponsiveMenuItem[] = [];

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {}

  ngOnInit(): void {
    this.user$
      .pipe(
        first((user) => !!user),
        switchMap((user) =>
          this.languageCode$.pipe(map((languageCode) => ({ user, languageCode }))),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(({ user }) => {
        this._setMenuItems(user);
      });
  }

  onLogout(): void {
    this._authenticationApi.logout().subscribe({
      next: () => {
        // Reset state and go to login
        this._authorizedStore.dispatch(AuthorizedActions.resetState());
        this._browserStorage.delete('spotlightUrl');
        this._router.navigate(['']).then();
      },
      error: () => {
        this._toast.error(this._language.translate('acs-header-menu.logout_failed'));
      },
    });
  }

  private _setMenuItems(user: AccredibleUser): void {
    this.dynamicMenuItems = [
      {
        id: 'rmi-myCredentials',
        label: this._language.selectTranslate('acs-header-menu.my_credentials'),
        externalLink: this._getRecipientPortalWalletUrl(user.username).href,
      },
      {
        id: 'rmi-courseFinder',
        label: this._language.selectTranslate('acs-header-menu.coursefinder'),
        externalLink: this._accountsRedirection.getCourseFinderUrl(),
      },
    ];

    if (user.profiles?.evaluator_profile) {
      this.dynamicMenuItems.push({
        id: 'rmi-mySpotlightDirectories',
        label: this._language.selectTranslate('acs-header-menu.my_directories'),
        externalLink: 'my-spotlight-directories',
      });
    }

    this.staticMenuItems = [
      {
        id: 'rmi-help',
        label: this._language.selectTranslate('acs-header-menu.help'),
        externalLink: this._language.selectTranslate('language.help_links.help_center'),
      },
      {
        id: 'rmi-signOut',
        divider: true,
        label: this._language.selectTranslate('acs-header-menu.sign_out'),
        actionBtn: this.onLogout.bind(this),
      },
    ];

    // We only want to show the 'back to directory' link if the user is coming from Spotlight
    const urlParams = new URLSearchParams(this._document.location.search);
    if (urlParams.get('app') === 'spotlight') {
      const spotlightUrl = this._getSpotlightUrl();
      this.dynamicMenuItems.unshift({
        id: 'rmi-spotlight',
        label: this._language.selectTranslate('acs-header-menu.back_to_directory'),
        externalLink: spotlightUrl.href,
      });
    }

    // If we have extra menu items we add them here
    if (this.extraDynamicMenuItems) {
      this.dynamicMenuItems = [...this.extraDynamicMenuItems, ...this.dynamicMenuItems];
    }
  }

  /**
   * This method return the Recipient Portal URL with all the required search params.
   */
  private _getRecipientPortalWalletUrl(username: string): URL {
    const urlParams = new URLSearchParams(this._document.location.search);
    const token = this._cookies.get(AccountsRedirectionKey.SESSION_TOKEN_COOKIE);
    const walletUrl = AccredibleUrlHelper.createUrl(
      `${
        this._browserStorage.get(AccountsRedirectionKey.RECIPIENT_PORTAL_URL) ||
        environment.credentialNetUrl
      }/profile/${username}/wallet`,
      'Accounts Recipient Portal wallet URL',
    );
    walletUrl.searchParams.set('token', token);
    return this._getUrlWithLanguageQueryParam(walletUrl, urlParams);
  }

  /**
   * This method is used to get the Spotlight URL in order to be used in the 'back to directory' link.
   *
   * We need to check for both HOST or ORIGIN search params because of the logic that is implemented right now,
   * If the user is logging in when coming from Spotlight we want the user to stay in accounts and in the search params we only have 'origin' and not 'host',
   * but if the user is coming from Spotlight and is already logged in we only have 'host' and not 'origin'
   */
  private _getSpotlightUrl(): URL {
    const urlParams = new URLSearchParams(this._document.location.search);
    const origin = urlParams.get(AccountsRedirectionKey.ORIGIN);
    const host = urlParams.get(AccountsRedirectionKey.HOST);
    const token = this._cookies.get(AccountsRedirectionKey.SESSION_TOKEN_COOKIE);
    const url = AccredibleUrlHelper.createUrl(`${origin || host}`, 'Accounts Spotlight URL');
    url.searchParams.set('token', token);
    return this._getUrlWithLanguageQueryParam(url);
  }

  private _getUrlWithLanguageQueryParam(url: URL, urlParams?: URLSearchParams): URL {
    const language =
      urlParams?.get(AccountsRedirectionKey.LANGUAGE) ||
      this._browserStorage.get(AccountsRedirectionKey.LANGUAGE);
    if (language) {
      url.searchParams.set(AccountsRedirectionKey.LANGUAGE, language);
    }
    return url;
  }
}
